import { ForgotPasswordModalComponent } from '$core/modal/forgot-password-modal/forgot-password-modal.component';
import { AppealService } from '$core/services/appeal.service';
import { EyeSightService } from '$core/services/eye-sight.service';
import { ScrollWidthService } from '$core/services/scroll-width.service';
import { SocketService } from '$core/services/socket.service';
import { UserService } from '$core/services/user.service';
import { environment } from '$env/environment';
import { SwalErrorService } from '$shared/components/swal-error/swal-error.service';
import { HttpErrorResponse } from '@angular/common/http';
import {
  AfterViewInit,
  Component,
  DestroyRef,
  HostBinding,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { Network } from '@ngx-pwa/offline';
import { TranslateService } from '@ngx-translate/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { of, switchMap } from 'rxjs';
import {
  BatchDocumentToastService,
  BatchDocumentsStoreService,
  BatchDocumentsService,
} from '$core/services';
import {
  BatchDocumentsStoreItem,
  DocumentDownloadResult,
} from '$core/services/batch-documents/batch-documents-handler';
import { BatchDocumentStatus } from '$modules/personal/modules/batch';
import { OverlayService, Overlays } from './overlay/overlay.service';
import { SwalService } from './swal/swal.service';
import { BatchRefreshService } from '$core/services/batch-documents/batch-refresh.service';
import { SessionService } from '$core/services/session.service';
import { distinctUntilChanged, filter, map } from 'rxjs';
import { UserAgreementUpdatingService } from './user-agreement-updating-modal';

@Component({
  selector: 'app-shell',
  templateUrl: './shell.component.html',
  styleUrls: ['./shell.component.scss'],
})
export class ShellComponent implements AfterViewInit {
  protected readonly eyeService = inject(EyeSightService);
  private readonly overlayService = inject(OverlayService);
  private readonly userService = inject(UserService);
  private readonly scrollWidthService = inject(ScrollWidthService);
  private readonly modalService = inject(BsModalService);
  private readonly swalService = inject(SwalService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly network = inject(Network);
  private readonly translateService = inject(TranslateService);
  private readonly swalErrorService = inject(SwalErrorService);
  private readonly socketService = inject(SocketService);
  private readonly appealService = inject(AppealService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly batchDocumentsService = inject(BatchDocumentsService);
  private readonly batchDocumentToastService = inject(
    BatchDocumentToastService
  );
  private readonly batchDocumentsStoreService = inject(
    BatchDocumentsStoreService
  );
  private readonly sessionService = inject(SessionService);
  private readonly batchRefreshService = inject(BatchRefreshService);
  private readonly userAgreementUpdatingService = inject(
    UserAgreementUpdatingService
  );

  public Overlays = Overlays;
  public isProduction = environment.production;

  public get isOverlayOpen() {
    return this.overlayService.isOpen;
  }

  public get queryParams() {
    return this.route.snapshot.queryParams;
  }

  public get scrollWidth$() {
    return this.scrollWidthService.scrollWidth$;
  }

  @HostBinding('class.offline') public get isOffline() {
    return !this.network.online;
  }

  constructor() {
    this.socketService.events$
      .pipe(
        switchMap((res) => {
          if (
            res.data.status &&
            (res.data.type === 'document' || res.data.type === 'one_document')
          ) {
            const loadingDocumentInfo: BatchDocumentsStoreItem = {
              batchId: res.data.data.list_id,
              departureId: res.data.data.item_id,
              documentType: res.data.data.document_type,
              documentStatus: res.data.status as BatchDocumentStatus,
            };

            const isInLoadingAllDocumentsStore =
              !!this.batchDocumentsStoreService
                .loadingBatchAllDocumentsStore()
                .find(
                  (item) =>
                    item.batchId === loadingDocumentInfo.batchId &&
                    item.documentStatus === 'processing'
                );

            if (res.data.type === 'document' && isInLoadingAllDocumentsStore) {
              if (res.data.status === 'done') {
                this.batchDocumentsStoreService.updateStoreProcessingItem({
                  documentInfo: loadingDocumentInfo,
                  errorOccurred: false,
                  isAllDocuments: true,
                });

                this.batchDocumentsService
                  .download(res.data.data.document_id)
                  .subscribe();

                const storeItem =
                  this.batchDocumentsStoreService.findSpecificStoreItem(
                    loadingDocumentInfo,
                    true
                  );

                this.showDocumentReadyToast({
                  documentInfo: storeItem,
                  documentId: res.data.data.document_id,
                  isAllDocuments: true,
                });
              } else if (res.data.status === 'error') {
                this.batchDocumentsStoreService.updateStoreProcessingItem({
                  documentInfo: loadingDocumentInfo,
                  errorOccurred: true,
                  isAllDocuments: true,
                });
              }
            }

            const loadingDocumentsStore =
              this.batchDocumentsStoreService.loadingBatchDocumentsStore();
            const isInLoadingDocumentsStore = !!(
              loadingDocumentsStore[loadingDocumentsStore.length - 1] || []
            ).find(
              (item) =>
                item.batchId === loadingDocumentInfo.batchId &&
                item.departureId === loadingDocumentInfo.departureId &&
                item.documentType === loadingDocumentInfo.documentType &&
                item.documentStatus === 'processing'
            );

            if (res.data.type === 'one_document' && isInLoadingDocumentsStore) {
              if (res.data.status === 'done') {
                this.batchDocumentsStoreService.updateStoreProcessingItem({
                  documentInfo: loadingDocumentInfo,
                  errorOccurred: false,
                  isAllDocuments: false,
                });

                const storeItem =
                  this.batchDocumentsStoreService.findSpecificStoreItem(
                    loadingDocumentInfo,
                    false
                  );

                if (
                  loadingDocumentsStore[loadingDocumentsStore.length - 1]
                    .length === 1
                ) {
                  this.batchDocumentsService
                    .download(res.data.data.document_id)
                    .subscribe();
                } else if (storeItem) {
                  this.showDocumentReadyToast({
                    documentInfo: storeItem,
                    documentId: res.data.data.document_id,
                    isAllDocuments: false,
                  });
                }
              } else if (res.data.status === 'error') {
                this.batchDocumentsStoreService.updateStoreProcessingItem({
                  documentInfo: loadingDocumentInfo,
                  errorOccurred: true,
                  isAllDocuments: false,
                });
              }
            }

            this.batchRefreshService.refresh(loadingDocumentInfo);
          }

          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private showDocumentReadyToast(res: DocumentDownloadResult): void {
    if (res.isAllDocuments) {
      this.batchDocumentToastService.showDocumentReadyToast({
        title: `${this.translateService.instant(
          'BATCH_MAILING.BATCHES_TAB.NOTIFICATION.ALL_DONE'
        )} ${res.documentInfo.labelsCount}`,
        subtitle: res.documentInfo.batchName ?? '',
      });
    } else {
      this.batchDocumentToastService.showDocumentReadyToast({
        title: this.translateService.instant(
          res.documentInfo.documentType === 'shipping_label'
            ? 'BATCH_MAILING.BATCHES_TAB.NOTIFICATION.LABEL_DONE'
            : 'BATCH_MAILING.BATCHES_TAB.NOTIFICATION.PARTIAL_RECEIPT_DONE'
        ),
        subtitle: res.documentInfo.s10Code ?? '',
        buttonName: this.translateService.instant('OPEN'),
        documentId: res.documentId!,
      });
    }
  }

  public ngAfterViewInit(): void {
    if (document.getElementById('app-spinner')) {
      document.getElementById('app-spinner').remove();
    }

    this.checkForgotNewPassword();
    this.checkEmailConfirmation();

    this.checkAppeal();

    this.observeForUserAgreementUpdate();
  }

  public checkAppeal() {
    const code = this.router
      .parseUrl(location.pathname + location.search)
      .queryParamMap.get('appeal_confirmation_code');

    if (code) {
      const swal = this.swalService.swal;

      const body = {
        code,
      };

      this.appealService.confirm(body).subscribe(
        (res: any) => {
          swal.icon = 'success';
          swal.title = this.translateService.instant('APPEAL_CONFIRM_SUCCESS');
          swal.text = `${this.translateService.instant(
            'APPEAL_CONFIRM_NUMBER'
          )} ${res.number} ${this.translateService.instant(
            'APPEAL_CONFIRM_SUCCESS_TEXT'
          )} `;
          swal.fire();
        },
        (err) => {
          swal.icon = 'error';
          swal.title = this.translateService.instant('APPEAL_CONFIRM_ERROR');
          swal.text = this.translateService.instant(
            'APPEAL_CONFIRM_ERROR_TEXT'
          );
          swal.fire();
        }
      );
      this.router.navigate(['/'], { replaceUrl: true });
    }
  }

  public checkEmailConfirmation() {
    const code: string = this.queryParams.email_code;

    if (!code) {
      return;
    }

    this.userService.confirmEmail({ code }).subscribe(
      (res) => {
        if (res.status === 'ok') {
          const swal = this.swalService.swal;
          swal.icon = 'success';
          swal.title = this.translateService.instant(
            'CONFIRMATION_EMAIL_SUCCESSFULL'
          );
          swal.fire();
        }
      },
      (err) => {
        this.onError(err);
      }
    );

    this.router.navigate([], {
      queryParams: { email_code: null },
      queryParamsHandling: 'merge',
      relativeTo: this.route,
    });
  }

  public checkForgotNewPassword() {
    const code = this.queryParams.code;

    if (!code) {
      return;
    }

    this.modalService.show(ForgotPasswordModalComponent, {
      class: 'modal-dialog-centered',
      initialState: {
        isAwaitingNewPassword: true,
        code,
      },
    });

    this.router.navigate([], {
      queryParams: { code: null },
      queryParamsHandling: 'merge',
      relativeTo: this.route,
    });
  }

  private observeForUserAgreementUpdate(): void {
    this.sessionService.session$
      .pipe(
        filter((session) => !!session),
        map((session) => !session.is_accept_terms),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((isUserAgreementNeedToBeAccepted) => {
        if (!isUserAgreementNeedToBeAccepted) return;

        this.userAgreementUpdatingService.request();
      });
  }

  private onError(error) {
    if (error.status === 422) {
      this.swalErrorService.showError.next({
        title: 'AN_ERROR_HAS_OCCURRED',
        text: this.swalErrorService.parseError(error.error),
      });
    } else {
      const res: HttpErrorResponse = error;
      const swal = this.swalService.swal;
      swal.icon = 'error';
      swal.title = this.translateService.instant('AN_ERROR_HAS_OCCURRED');
      swal.text = res.error.message || res.statusText;
      swal.fire();
    }
  }
}
